import React, { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { X, User } from 'lucide-react'

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px 30px;
  width: 90%;
  max-width: 500px;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
`

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }
`

const NicknameSection = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  padding: 30px;
  text-align: center;
  color: white;
  margin-bottom: 20px;
  margin-top: 20px;
`

const NicknameTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 8px 0;
`

const NicknameDescription = styled.p`
  font-size: 14px;
  margin: 0 0 20px 0;
  opacity: 0.9;
`

const NicknameInputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`

const NicknameInput = styled.input`
  width: 300px;
  max-width: 100%;
  padding: 15px 20px;
  border: none;
  border-radius: 10px;
  font-size: 18px;
  text-align: center;
  
  @media (max-width: 768px) {
    width: 100%;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
  
  &::placeholder {
    color: #999;
  }
`

const ConfirmButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid white;
  padding: 12px 15px;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: white;
    color: #764ba2;
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
    border-color: rgba(255, 255, 255, 0.3);
    cursor: not-allowed;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`

export default function NicknameEntryModal({ isOpen, onClose, onConfirm, roomCode }) {
  const [nickname, setNickname] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()

  const handleConfirm = async () => {
    if (!nickname.trim()) return

    setIsSubmitting(true)
    
    try {
      // 기존 onConfirm 콜백이 있다면 실행
      if (onConfirm) {
        await onConfirm(nickname.trim(), roomCode)
      }
      
      // AR 게임 페이지로 네비게이션
      navigate('/ar-game', {
        state: {
          nickname: nickname.trim(),
          roomCode: roomCode,
          userRank: Math.floor(Math.random() * 5) + 1, // 임시 랜덤 랭크
          initialScore: Math.floor(Math.random() * 3000) + 1000 // 임시 랜덤 점수
        }
      })
      
      // 모달 닫기
      onClose()
    } catch (error) {
      console.error('닉네임 설정 오류:', error)
      alert('닉네임 설정 중 오류가 발생했습니다.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setNickname('')
    onClose()
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleConfirm()
    }
  }

  if (!isOpen) return null

  return (
    <ModalBackdrop onClick={handleClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <X size={20} />
        </CloseButton>
        
        <NicknameSection>
          <NicknameTitle>닉네임 입력</NicknameTitle>
          <NicknameDescription>
            게임에서 사용할 닉네임을 입력해주세요
          </NicknameDescription>
          <NicknameInputContainer>
            <NicknameInput
              type="text"
              placeholder="닉네임 입력"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              onKeyPress={handleKeyPress}
              maxLength={10}
              autoFocus
            />
            <ConfirmButton onClick={handleConfirm} disabled={!nickname.trim() || isSubmitting}>
              <User size={16} />
              {isSubmitting ? '설정 중...' : '확인'}
            </ConfirmButton>
          </NicknameInputContainer>
        </NicknameSection>
      </ModalContent>
    </ModalBackdrop>
  )
}